import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthStore } from '../../store/useAuthStore';
import { usePersistedAuthStore } from '../../store/usePersistedAuthStore';
import { useTenantStore } from '../../store/useTenantStore';
import { useUserStore } from '../../store/useUserStore';
import { ApiResponse, LoginResponse, TenantAvailabilityResponse } from './types';
import { IS_DEV, USE_PROXY, BASE_SERVER_URL } from '../constants';
import { Capacitor } from '@capacitor/core';
import { parseApiError, logApiError, ApiError } from '../errors/apiErrors';
import { csrfTokenManager, initializeCSRFProtection } from '../security/csrf';

class ApiClient {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
      withCredentials: false, // Disable credentials to avoid CORS issues
    });

    // Initialize CSRF protection
    initializeCSRFProtection();

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const tenantStore = useTenantStore.getState();
        const authStore = useAuthStore.getState();

        // Check if running in Capacitor
        const isCapacitor = Capacitor.isNativePlatform();
        
        // For IsTenantAvailable, always use api.sinavza.com
        const isTenantCheck = config.url?.includes('IsTenantAvailable');
        
        if (isTenantCheck) {
          // IsTenantAvailable ALWAYS goes to api.sinavza.com
          config.baseURL = 'https://api.sinavza.com';
          // Remove any proxy headers
          delete config.headers['X-Target-URL'];
        } else {
          // All other services use tenant's serverRootAddress if available
          const targetUrl = tenantStore.serverRootAddress || 'https://api.sinavza.com';
          
          if (isCapacitor) {
            // In Capacitor, always use full URLs
            config.baseURL = targetUrl;
          } else if (IS_DEV) {
            // In development, use direct API calls to avoid proxy DNS issues
            config.baseURL = targetUrl;
          } else if (window.location.hostname.includes('vercel.app') || window.location.hostname === 'localhost') {
            // In Vercel deployment or localhost production, use our API proxy
            config.baseURL = '';
            config.headers['X-Target-URL'] = targetUrl;
          } else {
            // In other production environments, use full URL
            config.baseURL = targetUrl;
          }
        }

        // Add auth token if available
        if (authStore.accessToken && !authStore.isTokenExpired()) {
          config.headers.Authorization = `Bearer ${authStore.accessToken}`;
        }

        // Add tenant header if available (for all requests except IsTenantAvailable)
        if (tenantStore.tenantId && !isTenantCheck) {
          config.headers['Abp.TenantId'] = tenantStore.tenantId;
        }

        // Add CSRF token
        config.headers = csrfTokenManager.addToHeaders(config.headers) as any;

        return config;
      },
      (error) => {
        const apiError = parseApiError(error);
        logApiError(apiError, 'Request Interceptor');
        return Promise.reject(apiError);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle DNS/Network errors
        if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
          const tenantStore = useTenantStore.getState();
          console.error(`Cannot reach server: ${tenantStore.serverRootAddress || 'api.sinavza.com'}`);
          console.error('This might be due to:');
          console.error('1. DNS not resolving the subdomain');
          console.error('2. Server is down or unreachable');
          console.error('3. Network/Firewall blocking the connection');
          console.error('4. VPN required to access this server');
        }

        // Handle 401 errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refreshed = await this.refreshToken();
          if (refreshed) {
            return this.axiosInstance(originalRequest);
          } else {
            // Clear all auth data and redirect to login
            this.clearAllAuthData();
            window.location.href = '/login';
          }
        }

        const apiError = parseApiError(error);
        logApiError(apiError, 'Response Interceptor');
        return Promise.reject(apiError);
      }
    );
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const persistedAuthStore = usePersistedAuthStore.getState();
      const authStore = useAuthStore.getState();

      if (!persistedAuthStore.refreshToken) {
        return false;
      }

      // Use the public refreshTokenRequest method which handles tenant URL
      const response = await this.refreshTokenRequest(persistedAuthStore.refreshToken);

      if (response.data.success && response.data.result) {
        const { accessToken, expireInSeconds, refreshToken } = response.data.result;
        
        authStore.setAccessToken(accessToken, expireInSeconds);
        persistedAuthStore.setRefreshToken(refreshToken);
        
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  private clearAllAuthData() {
    const authStore = useAuthStore.getState();
    const persistedAuthStore = usePersistedAuthStore.getState();
    const userStore = useUserStore.getState();
    
    // Clear all stores
    authStore.clearAuth();
    persistedAuthStore.clearPersistedAuth();
    userStore.clearUserInfo();
    
    // Also clear localStorage to ensure clean state
    localStorage.removeItem('auth-storage');
    localStorage.removeItem('persisted-auth-storage');
    localStorage.removeItem('user-storage');
  }

  public get instance() {
    return this.axiosInstance;
  }

  // API methods
  public async checkTenantAvailability(tenancyName: string) {
    // IsTenantAvailable will be handled by interceptor to always use api.sinavza.com
    const response = await this.axiosInstance.post<ApiResponse<TenantAvailabilityResponse>>(
      '/api/services/app/Account/IsTenantAvailable',
      { tenancyName }
    );
    return response;
  }

  public async login(userNameOrEmailAddress: string, password: string) {
    return this.axiosInstance.post<ApiResponse<LoginResponse>>('/api/TokenAuth/Authenticate', {
      userNameOrEmailAddress,
      password,
      rememberClient: true,
    });
  }

  public async getCurrentLoginInfo() {
    return this.axiosInstance.get('/api/services/app/Session/GetCurrentLoginInformations');
  }

  public async createWhiteboardSession() {
    return this.axiosInstance.post('/api/services/app/WhiteboardAuth/RequestLoginCode', {});
  }

  public async checkWhiteboardSession(code: string, sessionId?: string) {
    return this.axiosInstance.post('/api/services/app/WhiteboardAuth/CheckSessionStatus', {
      sessionId: sessionId || '',
      code:code,
     
    });
  }

  public async getWhiteboardToken(code: string, sessionId?: string) {
    return this.axiosInstance.get('/api/services/app/WhiteboardAuth/GetWhiteboardToken', {
      params: {
        sessionId: sessionId || '',
        code: code,      
      }
    });
  }

  public async getAllTests(params?: {
    filter?: string;
    skipCount?: number;
    maxResultCount?: number;
  }) {
    return this.axiosInstance.get('/api/services/app/QbTests/GetAll', {
      params: {
        Filter: params?.filter || '',
        SkipCount: params?.skipCount || 0,
        MaxResultCount: params?.maxResultCount || 100
      }
    });
  }

  public async getQbTestsForWhiteBoard(params?: {
    filter?: string;
    titleFilter?: string;
    testTypeFilter?: number;
    donemId?: number;
    lessonId?: number;
    skipCount?: number;
    maxResultCount?: number;
    sorting?: string;
  }) {
    return this.axiosInstance.get('/api/services/app/QbTests/GetForWhiteBoardAll', {
      params: {
        Filter: params?.filter || '',
        TitleFilter: params?.titleFilter || '',
        TestTypeFilter: params?.testTypeFilter,
        DonemId: params?.donemId || 0,
        LessonId: params?.lessonId || 0,
        SkipCount: params?.skipCount || 0,
        MaxResultCount: params?.maxResultCount || 100,
        Sorting: params?.sorting || ''
      }
    });
  }

  public async getTest(id: string) {
    return this.axiosInstance.get(`/api/services/app/QbTests/GetQbTestForEdit`, {
      params: { id }
    });
  }

  public async getTestQuestions(testId: string) {
    return this.axiosInstance.get('/api/services/app/QbTestQuestions/GetQbQuestionsByTestId', {
      params: { testId }
    });
  }

  public async getQbTestForView(id: string) {
    return this.axiosInstance.get('/api/services/app/QbTests/GetQbTestForView', {
      params: { id }
    });
  }

  public async getQbTestForEdit(id: string) {
    return this.axiosInstance.get('/api/services/app/QbTests/GetQbTestForEdit', {
      params: { Id: id }
    });
  }

  public async getQbQuestionsByTestId(testId: string) {
    return this.axiosInstance.get('/api/services/app/QbTestQuestions/GetQbQuestionsByTestId', {
      params: { testId }
    });
  }

  public async refreshTokenRequest(refreshToken: string) {
    // Get tenant info directly from store
    const tenantStore = useTenantStore.getState();
    
    // Create a config object with tenant-specific base URL
    const config: any = {
      headers: {}
    };
    
    // Add tenant ID header if available
    if (tenantStore.tenantId) {
      config.headers['Abp.TenantId'] = tenantStore.tenantId;
    }
    
    if (tenantStore.serverRootAddress) {
      if (IS_DEV && USE_PROXY) {
        config.headers['X-Target-URL'] = tenantStore.serverRootAddress;
      } else if (window.location.hostname.includes('vercel.app') || window.location.hostname === 'localhost') {
        config.headers['X-Target-URL'] = tenantStore.serverRootAddress;
      } else {
        config.baseURL = tenantStore.serverRootAddress;
      }
    }
    
    // Send as query params (like my-whiteboard does)
    config.params = {
      refreshToken
    };
    
    return this.axiosInstance.post<ApiResponse<LoginResponse>>('/api/TokenAuth/RefreshToken', null, config);
  }

  // Generic HTTP methods for external usage
  public async get<T = any>(url: string, config?: any) {
    return this.axiosInstance.get<T>(url, config);
  }

  public async post<T = any>(url: string, data?: any, config?: any) {
    return this.axiosInstance.post<T>(url, data, config);
  }

  public async put<T = any>(url: string, data?: any, config?: any) {
    return this.axiosInstance.put<T>(url, data, config);
  }

  public async delete<T = any>(url: string, config?: any) {
    return this.axiosInstance.delete<T>(url, config);
  }
}

export const apiClient = new ApiClient();